import React, { useState, useLayoutEffect, useMemo } from "react";
import { Box, Tooltip } from "@mui/material";
import { GANTT_DEPENDENCY_COLORS, GANTT_DEPENDENCY_TYPES, GANTT_DEPENDENCY_LABELS, normalizeDependencyType } from "../../../shared/appConstants";
import { REGEX } from "../../../shared/regex";
import "./GanttDependencyArrows.scss";

const GAP_OFFSET = 12;
const MISSING_MIN_LENGTH = 80;
const MISSING_MAX_LENGTH = 180;
const MISSING_TAIL_MIN = 8;
const MISSING_ARROW_GAP = 6;
const MISSING_VERTICAL_JOG = 14;
const MISSING_STUB_OFFSET = 0;
const MISSING_MIN_HORIZONTAL = 6;
const EDGE_PADDING = 8;

const normalizeRelationshipType = (type) =>
  String(type || "blocks")
    .trim()
    .toUpperCase()
    .replace(REGEX.dependencyTypeToken, "_");

const sanitizeId = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");

const formatRelationshipLabel = (value) => {
  const normalized = normalizeDependencyType(value);
  if (!normalized) return GANTT_DEPENDENCY_TYPES.BLOCKS;
  if (normalized === 'blocks') return GANTT_DEPENDENCY_TYPES.BLOCKS;
  if (normalized === 'required for') return GANTT_DEPENDENCY_TYPES.REQUIRED_FOR;
  if (normalized === 'dependent on' || normalized === 'depends on') return GANTT_DEPENDENCY_LABELS.DEPENDENT_ON;
  return normalized
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const GanttDependencyArrows = ({ tasksByColumn, viewType, containerRef, isEnabled = false }) => {
  const [arrows, setArrows] = useState([]);
  const tooltipModifiers = useMemo(() => ([
    {
      name: "offset",
      options: { offset: [0, 6] }
    },
    {
      name: "preventOverflow",
      options: {
        boundary: containerRef?.current || "clippingParents",
        padding: 8
      }
    },
    {
      name: "flip",
      options: {
        fallbackPlacements: ["top", "bottom", "right", "left"]
      }
    }
  ]), [containerRef]);

  useLayoutEffect(() => {
    if (!isEnabled || viewType === "Quarter" || viewType === "Year") {
      setArrows([]);
      return;
    }

    const collectCapsules = () => {
      if (!containerRef?.current) return [];
      return Object.values(tasksByColumn).flatMap((column) =>
        column
          .map((item) => {
            const selector = `[data-task-id="${item.task.id}"]`;
            const el =
              containerRef.current.querySelector(selector) ||
              document.querySelector(selector);
            if (!el) return null;
            const rect = el.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const scrollLeft = containerRef.current.scrollLeft || 0;
            const scrollTop = containerRef.current.scrollTop || 0;
            return {
              id: item.task.id,
              left: Math.round(rect.left - containerRect.left + scrollLeft),
              right: Math.round(rect.right - containerRect.left + scrollLeft),
              top: Math.round(rect.top - containerRect.top + scrollTop),
              bottom: Math.round(rect.bottom - containerRect.top + scrollTop),
            };
          })
          .filter(Boolean)
      );
    };

    const HORIZONTAL_CLEARANCE = 6;
    const VERTICAL_CLEARANCE = 3;
    const intersectsHorizontal = (capsules, y, x1, x2, ignoreIds = []) => {
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);

      return capsules.some(c =>
        !ignoreIds.includes(c.id) &&
        y > (c.top - HORIZONTAL_CLEARANCE) && y < (c.bottom + HORIZONTAL_CLEARANCE) &&
        (
          (c.left > minX && c.left < maxX) ||
          (c.right > minX && c.right < maxX) ||
          (c.left <= minX && c.right >= maxX)
        )
      );
    };

    const calculateArrowPath = (
      capsules,
      sourceId,
      targetId,
      relationshipType,
      colour,
      startYOffset = 0,
      endYOffset = 0,
      startXOffset = 0,
      endXOffset = 0,
      containerWidth = null,
      containerHeight = null
    ) => {
      const source = capsules.find(c => c.id === sourceId);
      const target = capsules.find(c => c.id === targetId);
      if (!source || !target) return null;

      // Start with vertical anchors at capsule centres
      let startY = (source.top + source.bottom) / 2 + startYOffset;
      let endY = (target.top + target.bottom) / 2 + endYOffset;

      const countVerticalIntersections = (capsuleList, x, y1, y2, ignoreIds = []) => {
        const minY = Math.min(y1, y2);
        const maxY = Math.max(y1, y2);
        return capsuleList.reduce((count, c) => {
          if (ignoreIds.includes(c.id)) return count;
          const withinX = x > (c.left - VERTICAL_CLEARANCE) && x < (c.right + VERTICAL_CLEARANCE);
          const overlapsY = maxY > c.top && minY < c.bottom;
          return withinX && overlapsY ? count + 1 : count;
        }, 0);
      };

      const MAX_SHIFT = Number.isFinite(containerHeight) && containerHeight > 0
        ? Math.max(220, containerHeight)
        : 220;
      const STEP = 4;
      const findClearY = (initialY, x1, x2, ignoreIds = []) => {
        if (!intersectsHorizontal(capsules, initialY, x1, x2, ignoreIds)) return initialY;
        let shift = 0;
        while (shift < MAX_SHIFT) {
          shift += STEP;
          if (!intersectsHorizontal(capsules, initialY - shift, x1, x2, ignoreIds)) return initialY - shift;
          if (!intersectsHorizontal(capsules, initialY + shift, x1, x2, ignoreIds)) return initialY + shift;
        }
        return initialY; // fallback in extremely dense layouts
      };

      // 1) Fixed left-to-left edges for all dependencies
      let sourceEdgeX = source.left;
      let targetEdgeX = target.left;
      const sourceOutDir = -1;
      const targetOutDir = -1;

      let sourceStubX = sourceEdgeX + sourceOutDir * GAP_OFFSET + startXOffset;
      let targetStubX = targetEdgeX + targetOutDir * GAP_OFFSET + endXOffset;

      sourceStubX = Math.min(sourceStubX, source.left - 2);
      targetStubX = Math.min(targetStubX, target.left - 2);

      let stubStartY = findClearY(startY, sourceEdgeX, sourceStubX, [source.id]);
      let stubEndY = findClearY(endY, targetStubX, targetEdgeX, [target.id]);
      let midY = (stubStartY + stubEndY) / 2;
      midY = findClearY(midY, sourceStubX, targetStubX);

      const minX = Number.isFinite(containerWidth) && containerWidth > 0 ? EDGE_PADDING : null;
      const maxX = Number.isFinite(containerWidth) && containerWidth > 0 ? containerWidth - EDGE_PADDING : null;
      const clampX = (value) => {
        if (minX === null || maxX === null) return value;
        return Math.max(minX, Math.min(value, maxX));
      };

      const evaluateStubs = (candidateSourceStubX, candidateTargetStubX) => {
        const nextStubStartY = findClearY(startY, sourceEdgeX, candidateSourceStubX, [source.id]);
        const nextStubEndY = findClearY(endY, candidateTargetStubX, targetEdgeX, [target.id]);
        let nextMidY = (nextStubStartY + nextStubEndY) / 2;
        nextMidY = findClearY(nextMidY, candidateSourceStubX, candidateTargetStubX, [source.id, target.id]);
        const verticalCrossings =
          countVerticalIntersections(capsules, candidateSourceStubX, nextStubStartY, nextMidY, [source.id, target.id]) +
          countVerticalIntersections(capsules, candidateTargetStubX, nextMidY, nextStubEndY, [source.id, target.id]);
        const middleIntersection = intersectsHorizontal(capsules, nextMidY, candidateSourceStubX, candidateTargetStubX, [source.id, target.id]);
        return {
          candidateSourceStubX,
          candidateTargetStubX,
          nextStubStartY,
          nextStubEndY,
          nextMidY,
          verticalCrossings,
          middleIntersection
        };
      };

      // 3) Horizontal collision avoidance for all horizontals (no capsule at all,
      //    but we can ignore the capsule that the stub is attached to).

      // If the two vertical stubs are effectively aligned, snap them together
      // to avoid tiny horizontal dashes in the middle segment.
      const MAX_STUB_SHIFT = 80;
      const STUB_SHIFT_STEP = 6;
      let bestStubEval = evaluateStubs(sourceStubX, targetStubX);
      if (bestStubEval.verticalCrossings > 0 || bestStubEval.middleIntersection) {
        for (let shift = 0; shift <= MAX_STUB_SHIFT; shift += STUB_SHIFT_STEP) {
          const candidates = shift === 0
            ? [[sourceStubX, targetStubX]]
            : [
                [sourceStubX + shift, targetStubX + shift],
                [sourceStubX - shift, targetStubX - shift]
              ];

          for (const [candidateSource, candidateTarget] of candidates) {
            const evalResult = evaluateStubs(clampX(candidateSource), clampX(candidateTarget));
            if (evalResult.verticalCrossings === 0 && !evalResult.middleIntersection) {
              bestStubEval = evalResult;
              shift = MAX_STUB_SHIFT + STUB_SHIFT_STEP;
              break;
            }
            if (evalResult.verticalCrossings < bestStubEval.verticalCrossings) {
              bestStubEval = evalResult;
            }
          }
        }
      }

      sourceStubX = bestStubEval.candidateSourceStubX;
      targetStubX = bestStubEval.candidateTargetStubX;
      stubStartY = bestStubEval.nextStubStartY;
      stubEndY = bestStubEval.nextStubEndY;
      midY = bestStubEval.nextMidY;

      const STUB_SNAP_THRESHOLD = 3;
      if (Math.abs(sourceStubX - targetStubX) <= STUB_SNAP_THRESHOLD) {
        targetStubX = sourceStubX;
      }

      // 3a) Start/end stub horizontals: keep centre Y when possible,
      //     avoid OTHER capsules but allow touching their own capsule.
      //     (already computed during candidate evaluation)

      // 3b) Middle horizontal (shortest possible) between outer stubs
      //     (already computed during candidate evaluation)

      // 4) Arrow tip: just beyond target edge, pointing INTO the capsule
      //    - right edge: arrow points left  (tip just inside leftwards)
      //    - left edge:  arrow points right (tip just inside rightwards)
      const arrowTipX = targetEdgeX;

      // Final path:
      // - horizontal out of source
      // - vertical connector
      // - (optional) middle horizontal at clear Y
      // - vertical down/up near target
      // - horizontal into target edge
      const pathData = `
        M ${sourceEdgeX} ${stubStartY}
        L ${sourceStubX} ${stubStartY}
        L ${sourceStubX} ${midY}
        L ${targetStubX} ${midY}
        L ${targetStubX} ${stubEndY}
        L ${targetEdgeX} ${stubEndY}
        L ${arrowTipX} ${stubEndY}
      `;

      const relationshipKey = normalizeRelationshipType(relationshipType);
      const color =
        colour ||
        GANTT_DEPENDENCY_COLORS[relationshipKey] ||
        GANTT_DEPENDENCY_COLORS.BLOCKS;

      return {
        pathData,
        color,
        relationshipType: relationshipKey
      };
    };

    const calculateMissingArrowPath = (
      capsules,
      sourceId,
      relationshipType,
      colour,
      containerWidth,
      containerHeight,
      startYOffset = 0,
      startXOffset = 0
    ) => {
      const source = capsules.find(c => c.id === sourceId);
      if (!source) return null;

      const relationshipKey = normalizeRelationshipType(relationshipType);
      const color =
        colour ||
        GANTT_DEPENDENCY_COLORS[relationshipKey] ||
        GANTT_DEPENDENCY_COLORS.BLOCKS;

      const availableRight = Math.max(0, containerWidth - source.right - EDGE_PADDING);
      const dir = 1; // Always draw missing-target (dotted) arrows from the right edge
      const startEdgeX = source.right;
      // Keep missing-target stubs clearly outside the capsule
      let stubX = startEdgeX + dir * (GAP_OFFSET + MISSING_STUB_OFFSET) + startXOffset;
      const maxLen = Math.min(MISSING_MAX_LENGTH, availableRight);
      const desiredLen = Math.max(MISSING_MIN_LENGTH, maxLen);
      const unclampedEndX = stubX + dir * desiredLen;
      let endX = Math.min(unclampedEndX, containerWidth - EDGE_PADDING);
      // Ensure a small outward horizontal tail even when space is tight.
      const allowOverflow = maxLen < MISSING_TAIL_MIN;
      const tailMin = MISSING_TAIL_MIN;
      if (allowOverflow) {
        endX = stubX + dir * tailMin;
      } else {
        if (endX < stubX + tailMin) {
          endX = stubX + tailMin;
        }
      }

      const midY = (source.top + source.bottom) / 2 + startYOffset;
      const findClearY = (initialY, x1, x2) => {
        if (!intersectsHorizontal(capsules, initialY, x1, x2, [source.id])) return initialY;
        let shift = 0;
        const MAX_SHIFT = 100;
        const STEP = 4;
        while (shift < MAX_SHIFT) {
          shift += STEP;
          if (!intersectsHorizontal(capsules, initialY - shift, x1, x2, [source.id])) return initialY - shift;
          if (!intersectsHorizontal(capsules, initialY + shift, x1, x2, [source.id])) return initialY + shift;
        }
        return initialY;
      };
      const startClearY = findClearY(midY, startEdgeX, stubX);
      const jogDir =
        containerHeight && startClearY + MISSING_VERTICAL_JOG <= containerHeight - EDGE_PADDING ? 1 : -1;
      const jogY = startClearY + jogDir * MISSING_VERTICAL_JOG;
      const endClearY = findClearY(jogY, stubX, endX);
      const arrowTipY = endClearY + jogDir * MISSING_ARROW_GAP;
      const hasHorizontalSpace = maxLen >= MISSING_TAIL_MIN;

      const horizontalEndX = stubX + dir * MISSING_MIN_HORIZONTAL;
      const arrowTipX = horizontalEndX + dir * MISSING_ARROW_GAP;
      const pathData = hasHorizontalSpace
        ? `
          M ${startEdgeX} ${startClearY}
          L ${stubX} ${startClearY}
          L ${horizontalEndX} ${startClearY}
          L ${arrowTipX} ${startClearY}
        `
        : `
          M ${startEdgeX} ${startClearY}
          L ${stubX} ${startClearY}
          L ${stubX} ${endClearY}
          L ${stubX} ${arrowTipY}
        `;

      return {
        pathData,
        color,
        relationshipType: relationshipKey,
        isMissingTarget: true
      };
    };

    const computeArrows = () => {
      const capsules = collectCapsules();
      const containerWidth = containerRef.current?.scrollWidth || containerRef.current?.clientWidth || 0;
      const containerHeight = containerRef.current?.scrollHeight || containerRef.current?.clientHeight || 0;
      const capsuleIdSet = new Set(capsules.map(c => c.id));
      const taskById = new Map();
      Object.values(tasksByColumn).forEach(columnTasks => {
        columnTasks.forEach(item => {
          taskById.set(item.task.id, item.task);
        });
      });
      const newArrows = [];
      const deps = [];
      const seenTasks = new Set();
      Object.values(tasksByColumn).forEach(columnTasks => {
        columnTasks.forEach(item => {
          const task = item.task;
          if (seenTasks.has(task.id)) return;
          seenTasks.add(task.id);
          if (!task.dependencies?.length) return;
          task.dependencies.forEach(dep => {
            deps.push({ task, dep });
          });
        });
      });

      const sourceCounts = new Map();
      const targetCounts = new Map();
      deps.forEach(({ task, dep }) => {
        sourceCounts.set(task.id, (sourceCounts.get(task.id) || 0) + 1);
        if (dep.targetTaskId) {
          targetCounts.set(dep.targetTaskId, (targetCounts.get(dep.targetTaskId) || 0) + 1);
        }
      });

      const sourceSeen = new Map();
      const targetSeen = new Map();
      const outgoingBySource = new Map();
      const incomingByTarget = new Map();
      deps.forEach((entry, index) => {
        const sourceId = entry.task.id;
        if (!outgoingBySource.has(sourceId)) outgoingBySource.set(sourceId, []);
        outgoingBySource.get(sourceId).push({ ...entry, index });
        if (entry.dep.targetTaskId) {
          const targetId = entry.dep.targetTaskId;
          if (!incomingByTarget.has(targetId)) incomingByTarget.set(targetId, []);
          incomingByTarget.get(targetId).push({ ...entry, index });
        }
      });

      const hiddenOutgoing = new Set();
      const hiddenIncoming = new Set();

      const getTooltipLine = (task, dep, primaryTaskId = null) => {
        const sourceTicketNumber = task.ticketNumber || task.ticket_number || task.id;
        const targetTask = taskById.get(dep.targetTaskId);
        const targetTicketNumber =
          dep.targetTicketNumber ||
          targetTask?.ticketNumber ||
          targetTask?.ticket_number ||
          dep.targetTaskLabel ||
          dep.targetTaskId;
        const relationshipLabel = formatRelationshipLabel(dep.relationshipType);
        const primaryIsTarget = primaryTaskId && String(primaryTaskId) === String(dep.targetTaskId);
        if (primaryIsTarget) {
          return `${targetTicketNumber} ${relationshipLabel} ${sourceTicketNumber}`;
        }
        return `${sourceTicketNumber} ${relationshipLabel} ${targetTicketNumber}`;
      };
      outgoingBySource.forEach((list) => {
        if (list.length > 2) {
          list.slice(2).forEach(entry => hiddenOutgoing.add(entry.index));
        }
      });

      incomingByTarget.forEach((list) => {
        if (list.length > 2) {
          list.slice(2).forEach(entry => hiddenIncoming.add(entry.index));
        }
      });

      // Track visible arrow counts (after hiding) to decide if offsets are needed.
      // Standard arrows (left edge) and missing-target (dotted, right edge) are counted separately
      // so one side doesn't push the other away from the center.
      const visibleSourceCounts = new Map();
      const visibleTargetCounts = new Map();
      const visibleMissingSourceCounts = new Map();
      deps.forEach((entry, index) => {
        if (hiddenOutgoing.has(index) || hiddenIncoming.has(index)) return;
        const targetExists = entry.dep.targetTaskId && capsuleIdSet.has(entry.dep.targetTaskId);
        if (targetExists) {
          visibleSourceCounts.set(entry.task.id, (visibleSourceCounts.get(entry.task.id) || 0) + 1);
          visibleTargetCounts.set(entry.dep.targetTaskId, (visibleTargetCounts.get(entry.dep.targetTaskId) || 0) + 1);
        } else {
          visibleMissingSourceCounts.set(entry.task.id, (visibleMissingSourceCounts.get(entry.task.id) || 0) + 1);
        }
      });

      const OFFSET_STEP = 5;
      const X_OFFSET_STEP = 6;
      const LANE_COUNT = 6;
      const LANE_STEP = 3;

      deps.forEach(({ task, dep }, depIndex) => {
        const isHidden = hiddenOutgoing.has(depIndex) || hiddenIncoming.has(depIndex);
        if (isHidden) {
          return;
        }
        const sourceExists = capsuleIdSet.has(task.id);
        const targetExists = dep.targetTaskId && capsuleIdSet.has(dep.targetTaskId);
        if (!sourceExists || !targetExists) {
          // no-op: counts removed, keep flow unchanged
        }

        const sourceIndex = sourceSeen.get(task.id) || 0;
        const sourceTotal = targetExists
          ? (visibleSourceCounts.get(task.id) || 1)
          : (visibleMissingSourceCounts.get(task.id) || 1);
        const sourceTotalVisible = Math.min(sourceTotal, 2);
        sourceSeen.set(task.id, sourceIndex + 1);

        const targetIndex = targetSeen.get(dep.targetTaskId) || 0;
        const targetTotal = targetExists ? (visibleTargetCounts.get(dep.targetTaskId) || 1) : 1;
        const targetTotalVisible = Math.min(targetTotal, 2);
        if (dep.targetTaskId) {
          targetSeen.set(dep.targetTaskId, targetIndex + 1);
        }

        const shouldOffset = targetExists
          ? (sourceTotalVisible > 1 || targetTotalVisible > 1)
          : (sourceTotalVisible > 1);
        const laneOffset = shouldOffset
          ? ((depIndex % LANE_COUNT) - (LANE_COUNT - 1) / 2) * LANE_STEP
          : 0;

        const sourceOffset = sourceTotalVisible > 1
          ? (sourceIndex - (sourceTotalVisible - 1) / 2) * OFFSET_STEP + laneOffset
          : 0;
        const sourceXOffset = sourceTotalVisible > 1
          ? (sourceIndex - (sourceTotalVisible - 1) / 2) * X_OFFSET_STEP
          : 0;

        const targetOffset = targetTotalVisible > 1
          ? (targetIndex - (targetTotalVisible - 1) / 2) * OFFSET_STEP + laneOffset
          : 0;
        const targetXOffset = targetTotalVisible > 1
          ? (targetIndex - (targetTotalVisible - 1) / 2) * X_OFFSET_STEP
          : 0;

        const tooltip = getTooltipLine(task, dep);

        const arrow = targetExists
          ? calculateArrowPath(
              capsules,
              task.id,
              dep.targetTaskId,
              dep.relationshipType,
              dep.colour ?? dep.color,
              sourceOffset,
              targetOffset,
              sourceXOffset,
              targetXOffset,
              containerWidth,
              containerHeight
            )
          : calculateMissingArrowPath(
              capsules,
              task.id,
              dep.relationshipType,
              dep.colour ?? dep.color,
              containerWidth,
              containerHeight,
              sourceOffset,
              sourceXOffset
            );
        if (arrow) {
          newArrows.push({
            id: `${task.id}-${dep.targetTaskId}-${dep.relationshipType}`,
            targetLabel: dep.targetTaskLabel || dep.targetTaskId,
            tooltip,
            ...arrow
          });
        }
      });
      setArrows(newArrows);

    };

    const scheduleCompute = () => {
      computeArrows();
      requestAnimationFrame(() => {
        computeArrows();
      });
    };

    let rafId = null;
    let resizeObserver = null;
    let mutationObserver = null;
    let boundContainer = null;
    let destroyed = false;
    let cleanupScroll = null;
    let settleTimeout = null;
    let cleanupLoad = null;

    const setup = () => {
      if (destroyed) return;
      if (boundContainer) return;
      if (!containerRef.current) {
        rafId = requestAnimationFrame(setup);
        return;
      }

      boundContainer = containerRef.current;

      scheduleCompute();
      rafId = requestAnimationFrame(scheduleCompute);
      settleTimeout = setTimeout(() => {
        scheduleCompute();
      }, 120);

      resizeObserver = new ResizeObserver(scheduleCompute);
      resizeObserver.observe(boundContainer);

      mutationObserver = new MutationObserver(() => {
        scheduleCompute();
      });
      mutationObserver.observe(boundContainer, {
        childList: true,
        subtree: true,
        attributes: true
      });

      const handleScroll = () => scheduleCompute();
      boundContainer.addEventListener("scroll", handleScroll, { passive: true });
      cleanupScroll = () => {
        boundContainer.removeEventListener("scroll", handleScroll);
      };

      if (typeof window !== "undefined") {
        const handleLoad = () => scheduleCompute();
        window.addEventListener("load", handleLoad);
        cleanupLoad = () => window.removeEventListener("load", handleLoad);
      }

      if (typeof document !== "undefined" && document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (!destroyed) scheduleCompute();
        });
      }
    };

    setup();

    return () => {
      destroyed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
      if (cleanupScroll) cleanupScroll();
      if (cleanupLoad) cleanupLoad();
      if (settleTimeout) clearTimeout(settleTimeout);
    };
  }, [tasksByColumn, containerRef, isEnabled, viewType]);

  if (!isEnabled || arrows.length === 0) return null;

  return (
    <Box className="gantt-dependency-arrows">
      <svg className="gantt-dependency-svg" width="100%" height="100%">
        <defs>
          {arrows.map(arrow => {
            const markerId = `arrowhead-${sanitizeId(arrow.id)}`;
            return (
              <marker
                key={markerId}
                id={markerId}
                markerWidth="5"
                markerHeight="5"
                refX="4.5"
                refY="2.5"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L5,2.5 L0,5"
                  fill="none"
                  stroke={arrow.color}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
              </marker>
            );
          })}
        </defs>

        {arrows.map(arrow => (
          <Tooltip
            key={arrow.id}
            title={arrow.tooltip}
            placement="top"
            arrow
            classes={{ tooltip: "app-tooltip" }}
            disablePortal
            PopperProps={{ modifiers: tooltipModifiers }}
            followCursor
          >
            <g className="dependency-arrow-group">
              <path
                d={arrow.pathData}
                stroke="transparent"
                strokeWidth="10"
                fill="none"
                className="dependency-arrow-hit"
              />
              <path
                d={arrow.pathData}
                stroke={arrow.color}
                strokeWidth="2"
                fill="none"
                markerEnd={`url(#arrowhead-${sanitizeId(arrow.id)})`}
                className={`dependency-arrow${arrow.isMissingTarget ? " missing-target" : ""}`}
                strokeDasharray={arrow.isMissingTarget ? "4 4" : undefined}
                opacity="0.9"
              />
            </g>
          </Tooltip>
        ))}

      </svg>
    </Box>
  );
};

export default GanttDependencyArrows;
